import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { FlowTypes } from '@shared/models';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { WindowRef } from '@shared/services/window-ref/window-ref.service';
import { State } from '@store/reducers';
import {
  selectInheritanceDecedentSecurityToggle,
  selectMultiInheritanceEstateToggle,
  selectMultiInheritanceToggle,
  selectTriageState,
} from '@store/selectors';
import { catchError, map, Observable, of, switchMap, take, withLatestFrom } from 'rxjs';

import { getConfigClientFromRTE } from '../../../config/config-client';
import {
  External,
  Go,
  InheritedIraTriageContinue,
  LogError,
  LogInfo,
  MultiTriageContinue,
  Next,
  OverviewContinue,
  submitMetricsOAStart,
  TriageContinue,
  UpdateFlowTypeSelection,
  UpdateTriageSelections,
} from '../../actions';
import {
  getIsClient,
  getIsExecutorDecedentsSpouse,
  getIsIRAAccount,
  getIsIRAExecutor,
  getIsIRATrust,
  getIsMultiExecutor,
  getIsNotExecutorDecedentsSpouse,
  getIsOnlyTrustee,
  getIsProspect,
  getIsSingleExecutor,
  getIsTrusteeOrg,
  getIsTrustToTrust,
  getIsTrustToTrustFlow,
  TriageState,
} from '../../reducers/triage/triage.reducer';

@Injectable()
export class TriageSummaryEffects {
  constructor(
    private actions$: Actions,
    private globalStore$: Store<State>,
    private httpClient: HttpClient,
    private windowRef: WindowRef,
  ) {}

  getBaseHref(currentHref: string): string {
    if (currentHref.includes('/secure')) {
      return currentHref.split('/secure')[0];
    }

    let lengthCoefficient = 0;
    switch (true) {
      case currentHref.includes('multi-inheritance'):
        lengthCoefficient = '/multi-inheritance/summary'.length;
        break;
      case currentHref.includes('inherited-ira'):
        lengthCoefficient = '/inherited-ira/triage'.length;
        break;
      case currentHref.includes('/inheritance/overview'):
        lengthCoefficient = '/inheritance/overview'.length;
        break;
      case currentHref.includes('guide'):
        lengthCoefficient = '/guide'.length;
        break;
      case currentHref.includes('document-checklist'):
        lengthCoefficient = '/document-checklist/inheritance'.length;
        break;
      case currentHref.includes('inheritance'):
      default:
        lengthCoefficient = '/inheritance/summary'.length;
        break;
    }

    return currentHref.split('?')[0].slice(0, -lengthCoefficient);
  }

  multiTriageSummaryContinue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MultiTriageContinue),
        withLatestFrom(
          this.globalStore$.pipe(select(selectTriageState)),
          this.globalStore$.pipe(select(selectInheritanceDecedentSecurityToggle)),
        ),
        switchMap(([, triageState, isDecedentInfoSecurityEnabled]) => {
          LogInfo({
            payload: {
              eventId: 'multi-triage-summary-continue-effect-started',
              message: 'Multi triage summary continue effect start',
            },
          });
          const config = getConfigClientFromRTE();
          const baseHref = this.getBaseHref(this.windowRef.nativeWindow.location.href);
          const prospectUrl = config.pretty_ecs_domain + config.pvr_endpoint;
          const clientUrlPath = isDecedentInfoSecurityEnabled
            ? 'decedent-information'
            : 'decedent-info';
          const clientUrl = baseHref + '/secure/multi-inheritance/' + clientUrlPath;
          const url = getIsClient(triageState) ? clientUrl : prospectUrl;
          const actions: Action[] = [];
          actions.push(External({ url: url }));

          if (getIsProspect(triageState)) {
            actions.push(submitMetricsOAStart());

            const encryptURL = `pvr-encrypt-svc`;
            return this.httpClient
              .post(
                encryptURL,
                {
                  destinationUrl: clientUrl,
                  flowType: 'COO',
                  fallout: `${config.pretty_ecs_domain}${config.cwp_prefix}/multi-inheritance${config.ineligible_endpoint}`,
                },
                { responseType: 'text' },
              )
              .pipe(
                switchMap(() => actions),
                catchError(() => {
                  return [
                    Go({ path: ['/error'] }),
                    LogError({
                      payload: {
                        eventId: 'error-page-catch-pvr-encrypt-service',
                        message: 'PVR Encrypt Service Error',
                        code: LoggerCodes.NULL_FAILURE,
                      },
                    }),
                  ];
                }),
              );
          }
          return actions;
        }),
      ),
    { dispatch: true },
  );

  triageSummaryContinue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TriageContinue),
        withLatestFrom(this.globalStore$.pipe(select(selectTriageState))),
        switchMap(([, triageState]) => {
          LogInfo({
            payload: {
              eventId: 'triage-summary-continue-effect-started',
              message: 'Triage summary continue effect start',
            },
          });
          const config = getConfigClientFromRTE();
          const baseHref = this.getBaseHref(this.windowRef.nativeWindow.location.href);
          const url = this.getNavigationUrl(triageState, config, baseHref);
          const actions: Action[] = [];
          actions.push(External({ url: url }));

          if (getIsProspect(triageState)) {
            actions.push(submitMetricsOAStart());

            const encryptURL = `pvr-encrypt-svc`;
            return this.httpClient
              .post(
                encryptURL,
                {
                  destinationUrl: this.getPVRDestinationUrl(triageState, config, baseHref),
                  flowType: 'COO',
                  fallout: `${config.pretty_ecs_domain}${config.cwp_prefix}/inheritance${config.ineligible_endpoint}`,
                },
                { responseType: 'text' },
              )
              .pipe(
                switchMap(() => actions),
                catchError(() => {
                  return [
                    Go({ path: ['/error'] }),
                    LogError({
                      payload: {
                        eventId: 'error-page-catch-pvr-encrypt-service',
                        message: 'PVR Encrypt Service Error',
                        code: LoggerCodes.NULL_FAILURE,
                      },
                    }),
                  ];
                }),
              );
          }
          return actions;
        }),
      ),
    { dispatch: true },
  );

  InheritedIraTriageContinue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InheritedIraTriageContinue),
        withLatestFrom(this.globalStore$.pipe(select(selectTriageState))),
        switchMap(([, triageState]) => {
          LogInfo({
            payload: {
              eventId: 'inherited-ira-triage-continue-effect-started',
              message: 'inherited ira triage continue effect start',
            },
          });
          const config = getConfigClientFromRTE();
          const baseHref = this.getBaseHref(this.windowRef.nativeWindow.location.href);
          const prospectUrl = config.pretty_ecs_domain + config.pvr_endpoint;
          const clientUrl = baseHref + '/secure/inherited-ira/decedent-info';
          const url = getIsClient(triageState) ? clientUrl : prospectUrl;
          const actions: Action[] = [];
          actions.push(External({ url: url }));

          if (getIsProspect(triageState)) {
            actions.push(submitMetricsOAStart());

            const encryptURL = `pvr-encrypt-svc`;
            return this.httpClient
              .post(
                encryptURL,
                {
                  destinationUrl: clientUrl,
                  flowType: 'COO',
                  //no ineligible endpoint for inherited ira, just redirects back to cwp
                  fallout: `${config.pretty_ecs_domain}${config.cwp_prefix}/inherited-ira${config.ineligible_endpoint}`,
                },
                { responseType: 'text' },
              )
              .pipe(
                switchMap(() => actions),
                catchError(() => {
                  return [
                    Go({ path: ['/error'] }),
                    LogError({
                      payload: {
                        eventId: 'error-page-catch-pvr-encrypt-service',
                        message: 'PVR Encrypt Service Error',
                        code: LoggerCodes.NULL_FAILURE,
                      },
                    }),
                  ];
                }),
              );
          }
          return actions;
        }),
      ),
    { dispatch: true },
  );

  overviewContinue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverviewContinue),
        switchMap(() =>
          this.checkAuth().pipe(
            withLatestFrom(
              this.globalStore$.pipe(select(selectMultiInheritanceToggle)),
              this.globalStore$.pipe(select(selectMultiInheritanceEstateToggle)),
              this.globalStore$.pipe(select(selectInheritanceDecedentSecurityToggle)),
            ),
          ),
        ),
        switchMap(
          ([
            isAuthenticated,
            isMultiInheritanceFlow,
            isMultiInheritanceEstateFlow,
            isDecedentInfoSecurityEnabled,
          ]) => {
            const actions: Action[] = [
              LogInfo({
                payload: {
                  eventId: 'overview-continue-effect-started',
                  message: 'Overview Continue effect start',
                },
              }),
            ];

            const skipTriagePage = isMultiInheritanceFlow && isMultiInheritanceEstateFlow;
            this.globalStore$.dispatch(
              LogInfo({
                payload: {
                  eventId: 'overview-continue-effect',
                  message:
                    'User autheticated ' +
                    isAuthenticated +
                    ' and skipTriagePage ' +
                    skipTriagePage,
                },
              }),
            );
            if (isAuthenticated && skipTriagePage) {
              const baseHref = this.getBaseHref(this.windowRef.nativeWindow.location.href);
              const clientUrlPath = isDecedentInfoSecurityEnabled
                ? 'decedent-information'
                : 'decedent-info';
              const clientUrl = baseHref + '/secure/multi-inheritance/' + clientUrlPath;
              actions.push(
                UpdateTriageSelections({ payload: { isClient: 'YES' } }),
                UpdateFlowTypeSelection({ payload: FlowTypes.MULTI_INHERITANCE }),
                External({ url: clientUrl }),
              );
            } else if (!isAuthenticated && skipTriagePage) {
              actions.push(
                UpdateTriageSelections({ payload: { isClient: 'NO' } }),
                UpdateFlowTypeSelection({ payload: FlowTypes.MULTI_INHERITANCE }),
                MultiTriageContinue(),
              );
            } else {
              actions.push(Next());
            }

            return actions;
          },
        ),
      ),
    { dispatch: true },
  );

  private checkAuth(): Observable<boolean> {
    if (getConfigClientFromRTE().isInternal) {
      return of(true);
    }
    return this.httpClient
      .post<{ poid: string; sPoid: string }>('secure/auth', {}, { withCredentials: true })
      .pipe(
        map(() => true),
        take(1),
        catchError(() => {
          this.globalStore$.dispatch(
            LogError({
              payload: {
                eventId: 'check-auth-failure',
                message: 'Auth check failed',
                code: LoggerCodes.NULL_FAILURE,
              },
            }),
          );
          return of(false);
        }),
      );
  }

  private getPVRDestinationUrl(triageState: TriageState, config: any, baseHref: string): string {
    const isIRAMultiExecutor =
      getIsIRAAccount(triageState) &&
      getIsIRAExecutor(triageState) &&
      getIsMultiExecutor(triageState);
    const pvrDestinationUrl = baseHref + '/secure/inheritance/decedent-info';
    const multiExecutorPVRDestinationUrl =
      config.pretty_ecs_domain + config.trustMultisignerIntelliDocs_endpoint;

    const isTrustee = getIsIRATrust(triageState);
    const isTrusteeOrg = getIsTrusteeOrg(triageState);
    const isSingleTrustee = getIsOnlyTrustee(triageState);

    if (isTrustee) {
      if (isTrusteeOrg || !isSingleTrustee) {
        return multiExecutorPVRDestinationUrl;
      }
      return pvrDestinationUrl;
    }

    return isIRAMultiExecutor ? multiExecutorPVRDestinationUrl : pvrDestinationUrl;
  }

  private getNavigationUrl(triageState: TriageState, config: any, baseHref: string): string {
    const cooURL = baseHref + '/secure/inheritance/decedent-info';
    const iraEstateCooURL = baseHref + '/secure/inheritance/personal-info';
    const iraTrusteeUrl = baseHref + '/secure/inheritance/personal-info';
    const trustToTrustUrl = baseHref + '/secure/inheritance/personal-info';
    const ineligibleUrl = `${baseHref}/inheritance${config.ineligible_endpoint}`;
    const trustMultisignerEndpoint = config.trustMultisignerIntelliDocs_endpoint;
    const isClient = getIsClient(triageState);
    const isTrustToTrust = getIsTrustToTrust(triageState);
    const isTrustToTrustFlow = getIsTrustToTrustFlow(triageState);

    if (isTrustToTrust) {
      if (isTrustToTrustFlow && isClient) {
        return trustToTrustUrl;
      }
      if (!isTrustToTrustFlow) {
        return ineligibleUrl;
      }
    }

    const isNotExecutorDecedentsSpouse = getIsNotExecutorDecedentsSpouse(triageState);
    if (isNotExecutorDecedentsSpouse) {
      return ineligibleUrl;
    }

    if (!isClient) {
      return config.pretty_ecs_domain + config.pvr_endpoint;
    }

    const isIRAAccount = getIsIRAAccount(triageState);
    const isIRAExecutor = getIsIRAExecutor(triageState);
    const isSingleExecutor = getIsSingleExecutor(triageState);
    const isExecutorDecedentsSpouse = getIsExecutorDecedentsSpouse(triageState);
    if (isIRAAccount && isIRAExecutor && !isExecutorDecedentsSpouse) {
      return isSingleExecutor
        ? iraEstateCooURL
        : config.pretty_ecs_domain + trustMultisignerEndpoint;
    }

    const isTrustee = getIsIRATrust(triageState);
    const isTrusteeOrg = getIsTrusteeOrg(triageState);
    const isSingleTrustee = getIsOnlyTrustee(triageState);
    if (isTrustee) {
      if (isTrusteeOrg || !isSingleTrustee) {
        return config.pretty_ecs_domain + trustMultisignerEndpoint;
      }
      return iraTrusteeUrl;
    }

    return cooURL;
  }
}

export const effects: any[] = [TriageSummaryEffects];
